import { useState } from "react";
import { useCustomMutation, type useTranslate } from "@refinedev/core";
import { Button, Field, Modal } from "@fawkes/ui";

// communicatie: the "Testmail versturen" dialog (mirrors the Jmix TestMailDialogView intent). Editor-
// gated by the host. POSTs the recipient to communicatie/templates/{id}/test-mail (RELATIVE — the
// dataProvider prepends /api once) and surfaces the structured {sent, mailConfigured, message} result:
//   mailConfigured:false → a clear "mail niet geconfigureerd" notice (not an error)
//   sent:true            → a success notice
//   blank recipient      → blocked client-side (the api also 400s)

type Translate = ReturnType<typeof useTranslate>;

interface TestMailResult {
  sent: boolean;
  mailConfigured: boolean;
  message: string | null;
}

export function TestMailDialog({
  templateId,
  open,
  onClose,
  t,
}: {
  templateId: string;
  open: boolean;
  onClose: () => void;
  t: Translate;
}) {
  const { mutate, isLoading } = useCustomMutation();
  const [recipient, setRecipient] = useState("");
  const [notConfigured, setNotConfigured] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setNotConfigured(false);
    setSentTo(null);
    setError(null);
  }

  function close() {
    reset();
    setRecipient("");
    onClose();
  }

  function send() {
    const to = recipient.trim();
    reset();
    if (!to) {
      setError(t("communicatieEditor.testMail.error.required"));
      return;
    }
    mutate(
      {
        url: `communicatie/templates/${templateId}/test-mail`,
        method: "post",
        values: { recipient: to },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: (res) => {
          const r = res.data as unknown as TestMailResult;
          if (r.mailConfigured === false) {
            setNotConfigured(true);
            return;
          }
          if (r.sent) {
            setSentTo(to);
            return;
          }
          // mail configured but not sent → show the server message if any.
          setError(r.message || t("communicatieEditor.testMail.error.generic"));
        },
        onError: (e) => {
          setError((e as { message?: string })?.message ?? t("communicatieEditor.testMail.error.generic"));
        },
      },
    );
  }

  return (
    <Modal
      open={open}
      title={t("communicatieEditor.testMail.title")}
      onClose={close}
      footer={
        <>
          <Button variant="secondary" onClick={close} disabled={isLoading}>
            {t("common.cancel")}
          </Button>
          <Button variant="primary" onClick={send} disabled={isLoading || !recipient.trim()}>
            {t("communicatieEditor.testMail.send")}
          </Button>
        </>
      }
    >
      <p className="comm-editor__testmail-intro">{t("communicatieEditor.testMail.intro")}</p>
      <Field
        label={t("communicatieEditor.testMail.recipient")}
        type="email"
        invalidEmailMessage={t("validatie.emailadres")}
        placeholder={t("communicatieEditor.testMail.placeholder")}
        value={recipient}
        onChange={(e) => setRecipient(e.target.value)}
      />
      <p className="comm-editor__testmail-note">{t("communicatieEditor.testMail.note")}</p>

      {notConfigured && (
        <div className="comm-editor__alert comm-editor__alert--conflict" role="alert">
          {t("communicatieEditor.testMail.notConfigured")}
        </div>
      )}
      {error && (
        <div className="comm-editor__alert comm-editor__alert--error" role="alert">
          {error}
        </div>
      )}
      {sentTo && (
        <div className="comm-editor__alert comm-editor__alert--ok" role="status">
          {t("communicatieEditor.testMail.sent", { args: [sentTo] })}
        </div>
      )}
    </Modal>
  );
}
